import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth, homeFor } from "@/context/AuthContext";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import MyWork from "@/pages/MyWork";
import Portal from "@/pages/Portal";
import Clients from "@/pages/Clients";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import Tasks from "@/pages/Tasks";
import Goals from "@/pages/Goals";
import Proposals from "@/pages/Proposals";
import ProposalDetail from "@/pages/ProposalDetail";
import Accounts from "@/pages/Accounts";
import Team from "@/pages/Team";
import Settings from "@/pages/Settings";
import ClientDetail from "@/pages/ClientDetail";
import { PortalLibrary, PortalProduct, PortalOrders, PortalTimeline, PortalInProcess, PortalContent, PortalReports, PortalRetainer } from "@/pages/portal/PortalPages";

function Loading() {
  return (
    <div className="min-h-screen grid place-items-center bg-slate-950" data-testid="auth-loading">
      <div className="flex items-center gap-3 text-slate-300 text-sm">
        <span className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" /> Loading your workspace…
      </div>
    </div>
  );
}

function AuthCallback() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const processed = useRef(false);
  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sessionId = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/session", { session_id: sessionId });
        setUser(data);
        window.history.replaceState(null, "", window.location.pathname);
        navigate(homeFor(data), { replace: true, state: { user: data } });
      } catch {
        setUser(false);
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, setUser]);
  return <Loading />;
}

function Protected({ roles, children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homeFor(user)} replace />;
  return <Layout>{children}</Layout>;
}

function Home() {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  return <Navigate to={user ? homeFor(user) : "/login"} replace />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Login mode="register" />} />
      <Route path="/dashboard" element={<Protected roles={["admin"]}><Dashboard /></Protected>} />
      <Route path="/my-work" element={<Protected roles={["admin", "employee"]}><MyWork /></Protected>} />
      <Route path="/portal" element={<Protected roles={["client"]}><Portal /></Protected>} />
      <Route path="/portal/products" element={<Protected roles={["client"]}><PortalLibrary /></Protected>} />
      <Route path="/portal/products/:pid" element={<Protected roles={["client"]}><PortalProduct /></Protected>} />
      <Route path="/portal/orders" element={<Protected roles={["client"]}><PortalOrders /></Protected>} />
      <Route path="/portal/timeline" element={<Protected roles={["client"]}><PortalTimeline /></Protected>} />
      <Route path="/portal/in-process" element={<Protected roles={["client"]}><PortalInProcess /></Protected>} />
      <Route path="/portal/content" element={<Protected roles={["client"]}><PortalContent /></Protected>} />
      <Route path="/portal/reports" element={<Protected roles={["client"]}><PortalReports /></Protected>} />
      <Route path="/portal/retainer" element={<Protected roles={["client"]}><PortalRetainer /></Protected>} />
      <Route path="/clients" element={<Protected roles={["admin", "employee"]}><Clients /></Protected>} />
      <Route path="/clients/:id/*" element={<Protected roles={["admin", "employee"]}><ClientDetail /></Protected>} />
      <Route path="/projects" element={<Protected><Projects /></Protected>} />
      <Route path="/projects/:id" element={<Protected><ProjectDetail /></Protected>} />
      <Route path="/tasks" element={<Protected roles={["admin", "employee"]}><Tasks /></Protected>} />
      <Route path="/goals" element={<Protected roles={["admin", "employee"]}><Goals /></Protected>} />
      <Route path="/proposals" element={<Protected><Proposals /></Protected>} />
      <Route path="/proposals/:id" element={<Protected><ProposalDetail /></Protected>} />
      <Route path="/accounts" element={<Protected roles={["admin", "client"]}><Accounts /></Protected>} />
      <Route path="/team" element={<Protected roles={["admin", "employee"]}><Team /></Protected>} />
      <Route path="/settings" element={<Protected roles={["admin"]}><Settings /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" richColors closeButton />
      </BrowserRouter>
    </AuthProvider>
  );
}
